import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { findFirstNonEmptyString } from "scmp-sdk";
import "swiper/css";
import { Navigation } from "swiper/modules";
import { SwiperSlide } from "swiper/react";

import type { ResponsiveVariants } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import { BaseImage } from "scmp-app/components/common/base-image";
import { useResponsiveImage } from "scmp-app/components/common/base-image/hooks";
import { ContentPlaceholder } from "scmp-app/components/content/content-placeholder";
import type {
  contentCoverImageContent$data,
  contentCoverImageContent$key,
} from "scmp-app/queries/__generated__/contentCoverImageContent.graphql";

import { CarouselControls } from "./carousel-controls";
import { useCarousel } from "./hooks";
import { Figure, StyledFigcaption, StyledSwiper } from "./styles";

type CoverImageElement = NonNullable<NonNullable<contentCoverImageContent$data["images"]>[number]>;

type Props = {
  className?: string;
  reference: contentCoverImageContent$key;
  responsiveVariants: ResponsiveVariants<VariantKey>;
  shouldShowDefaultImage?: boolean;
  variant?: "plus" | undefined;
  withCaption?: boolean;
  withCarousel?: { imageType: string };
};

type VariantKey =
  | "landscape250x99"
  | "size80x80"
  | "size118x118"
  | "size320x180"
  | "size320x240"
  | "size500x500"
  | "size540x360"
  | "size640x360"
  | "size660w"
  | "size768x768"
  | "size800x1200"
  | "size1200x800"
  | "size1200x1200"
  | "size1280x720";

function getVariantStyledUrl(
  image: CoverImageElement | null | undefined,
  variant: VariantKey,
): string | undefined {
  switch (variant) {
    case "landscape250x99":
      return image?.landscape250x99?.url ?? undefined;
    case "size80x80":
      return image?.size80x80?.url ?? undefined;
    case "size118x118":
      return image?.size118x118?.url ?? undefined;
    case "size320x180":
      return image?.size320x180?.url ?? undefined;
    case "size320x240":
      return image?.size320x240?.url ?? undefined;
    case "size500x500":
      return image?.size500x500?.url ?? undefined;
    case "size540x360":
      return image?.size540x360?.url ?? undefined;
    case "size640x360":
      return image?.size640x360?.url ?? undefined;
    case "size660w":
      return image?.size660w?.url ?? undefined;
    case "size768x768":
      return image?.size768x768?.url ?? undefined;
    case "size800x1200":
      return image?.size800x1200?.url ?? undefined;
    case "size1200x800":
      return image?.size1200x800?.url ?? undefined;
    case "size1200x1200":
      return image?.size1200x1200?.url ?? undefined;
    case "size1280x720":
      return image?.size1280x720?.url ?? undefined;
  }
}

const VARIANT_SPECS: Record<VariantKey, { aspectRatio: string; width: number }> = {
  landscape250x99: { aspectRatio: "250/99", width: 2000 },
  size80x80: { aspectRatio: "1/1", width: 80 },
  size118x118: { aspectRatio: "1/1", width: 118 },
  size320x180: { aspectRatio: "4/3", width: 320 },
  size320x240: { aspectRatio: "4/3", width: 320 },
  size500x500: { aspectRatio: "1/1", width: 500 },
  size540x360: { aspectRatio: "3/2", width: 540 },
  size640x360: { aspectRatio: "16/9", width: 640 },
  size660w: { aspectRatio: "260/183", width: 660 },
  size768x768: { aspectRatio: "1/1", width: 768 },
  size800x1200: { aspectRatio: "2/3", width: 800 },
  size1200x800: { aspectRatio: "3/2", width: 1200 },
  size1200x1200: { aspectRatio: "1/1", width: 1200 },
  size1280x720: { aspectRatio: "16/9", width: 1280 },
};

export const ContentCoverImage: FunctionComponent<Props> = ({
  className,
  reference,
  responsiveVariants,
  shouldShowDefaultImage,
  variant,
  withCaption,
  withCarousel,
}) => {
  const content = useFragment(
    graphql`
      fragment contentCoverImageContent on Content
      @argumentDefinitions(
        withLandscape250x99: { type: "Boolean", defaultValue: false }
        withSize1200x800: { type: "Boolean", defaultValue: false }
        withSize1200x1200: { type: "Boolean", defaultValue: false }
        withSize1280x720: { type: "Boolean", defaultValue: false }
        withSize640x360: { type: "Boolean", defaultValue: false }
        withSize768x768: { type: "Boolean", defaultValue: false }
        withSize540x360: { type: "Boolean", defaultValue: false }
        withSize320x240: { type: "Boolean", defaultValue: false }
        withSize320x180: { type: "Boolean", defaultValue: false }
        withSize118x118: { type: "Boolean", defaultValue: false }
        withSize80x80: { type: "Boolean", defaultValue: false }
        withSize500x500: { type: "Boolean", defaultValue: false }
        withSize800x1200: { type: "Boolean", defaultValue: false }
        withSize660w: { type: "Boolean", defaultValue: false }
        withLeadingSlides: { type: "Boolean", defaultValue: false }
      ) {
        headline
        images {
          url
          title
          type
          landscape250x99: style(filter: { style: "landscape_250_99" })
            @include(if: $withLandscape250x99) {
            url
          }
          size1200x800: style(filter: { style: "1200x800" }) @include(if: $withSize1200x800) {
            url
          }
          size1200x1200: style(filter: { style: "1200x1200" }) @include(if: $withSize1200x1200) {
            url
          }
          size1280x720: style(filter: { style: "wide_landscape" }) @include(if: $withSize1280x720) {
            url
          }
          size640x360: style(filter: { style: "wide_landscape_640_360" })
            @include(if: $withSize640x360) {
            url
          }
          size768x768: style(filter: { style: "768x768" }) @include(if: $withSize768x768) {
            url
          }
          size540x360: style(filter: { style: "540x360" }) @include(if: $withSize540x360) {
            url
          }
          size320x240: style(filter: { style: "320x240" }) @include(if: $withSize320x240) {
            url
          }
          size320x180: style(filter: { style: "320x180" }) @include(if: $withSize320x180) {
            url
          }
          size80x80: style(filter: { style: "80x80" }) @include(if: $withSize80x80) {
            url
          }
          size118x118: style(filter: { style: "118x118" }) @include(if: $withSize118x118) {
            url
          }
          size500x500: style(filter: { style: "500x500" }) @include(if: $withSize500x500) {
            url
          }
          size800x1200: style(filter: { style: "portrait" }) @include(if: $withSize800x1200) {
            url
          }
          size660w: style(filter: { style: "660w" }) @include(if: $withSize660w) {
            url
          }
        }
        ... on Article @include(if: $withLeadingSlides) {
          leadingSlides {
            url
            title
            type
            landscape250x99: style(filter: { style: "landscape_250_99" })
              @include(if: $withLandscape250x99) {
              url
            }
            size1200x800: style(filter: { style: "1200x800" }) @include(if: $withSize1200x800) {
              url
            }
            size1200x1200: style(filter: { style: "1200x1200" }) @include(if: $withSize1200x1200) {
              url
            }
            size1280x720: style(filter: { style: "wide_landscape" })
              @include(if: $withSize1280x720) {
              url
            }
            size640x360: style(filter: { style: "wide_landscape_640_360" })
              @include(if: $withSize640x360) {
              url
            }
            size768x768: style(filter: { style: "768x768" }) @include(if: $withSize768x768) {
              url
            }
            size540x360: style(filter: { style: "540x360" }) @include(if: $withSize540x360) {
              url
            }
            size320x240: style(filter: { style: "320x240" }) @include(if: $withSize320x240) {
              url
            }
            size320x180: style(filter: { style: "320x180" }) @include(if: $withSize320x180) {
              url
            }
            size80x80: style(filter: { style: "80x80" }) @include(if: $withSize80x80) {
              url
            }
            size118x118: style(filter: { style: "118x118" }) @include(if: $withSize118x118) {
              url
            }
            size500x500: style(filter: { style: "500x500" }) @include(if: $withSize500x500) {
              url
            }
            size800x1200: style(filter: { style: "portrait" }) @include(if: $withSize800x1200) {
              url
            }
            size660w: style(filter: { style: "660w" }) @include(if: $withSize660w) {
              url
            }
          }
        }
      }
    `,
    reference,
  );

  const {
    activeIndex,
    handleDotClick,
    handleNext,
    handlePrevious,
    handleSlideChange,
    swiperReference,
  } = useCarousel();

  const firstCarouselImage = useMemo(() => {
    if (!withCarousel) return null;
    return (
      content.images?.find(image => image?.type === withCarousel.imageType) ??
      first(content.images) ??
      null
    );
  }, [withCarousel, content.images]);

  const carouselLeadingSlides = useMemo(
    () => (withCarousel ? (content.leadingSlides ?? []).filter(notEmpty) : []),
    [withCarousel, content.leadingSlides],
  );

  const carouselCount = (firstCarouselImage ? 1 : 0) + carouselLeadingSlides.length;
  const hasMultipleImages = carouselCount > 1;

  const mainImage =
    content.images?.find(image => image?.type === "mainImage") ?? first(content.images);

  const displayImage = shouldShowDefaultImage
    ? (content.images?.find(image => image?.type === "default") ?? first(content.images))
    : mainImage;

  const mapVariant = (viewportSize: "desktopUp" | "mobileUp" | "tabletUp") => ({
    aspectRatio: VARIANT_SPECS[responsiveVariants[viewportSize]].aspectRatio,
    url: getVariantStyledUrl(displayImage, responsiveVariants[viewportSize]) ?? "",
    width: VARIANT_SPECS[responsiveVariants[viewportSize]].width,
  });

  const mapImageVariant = (
    image: CoverImageElement | null | undefined,
    viewportSize: "desktopUp" | "mobileUp" | "tabletUp",
  ) => ({
    aspectRatio: VARIANT_SPECS[responsiveVariants[viewportSize]].aspectRatio,
    url: getVariantStyledUrl(image, responsiveVariants[viewportSize]) ?? image?.url ?? "",
    width: VARIANT_SPECS[responsiveVariants[viewportSize]].width,
  });

  const responsiveVariant = {
    desktopUp: mapVariant("desktopUp"),
    mobileUp: mapVariant("mobileUp"),
    tabletUp: mapVariant("tabletUp"),
  };

  const { bindResponsiveAspectRatio } = useResponsiveImage(responsiveVariant);
  const imageAlt = findFirstNonEmptyString(displayImage?.title, content.headline) ?? undefined;

  if (hasMultipleImages) {
    return (
      <>
        <StyledSwiper
          className={className}
          modules={[Navigation]}
          onSlideChange={handleSlideChange}
          ref={swiperReference}
          slidesPerView={1}
          {...bindResponsiveAspectRatio}
        >
          {firstCarouselImage && (
            <SwiperSlide key={firstCarouselImage.url ?? "first"}>
              <BaseImage
                $objectFit="cover"
                alt={
                  findFirstNonEmptyString(firstCarouselImage.title, content.headline) ?? undefined
                }
                responsiveVariant={{
                  desktopUp: mapImageVariant(firstCarouselImage, "desktopUp"),
                  mobileUp: mapImageVariant(firstCarouselImage, "mobileUp"),
                  tabletUp: mapImageVariant(firstCarouselImage, "tabletUp"),
                }}
                src={firstCarouselImage.url}
                {...bindResponsiveAspectRatio}
              />
            </SwiperSlide>
          )}
          {carouselLeadingSlides.map(slide => (
            <SwiperSlide key={slide.url}>
              <BaseImage
                $objectFit="cover"
                alt={findFirstNonEmptyString(slide.title, content.headline) ?? undefined}
                lazyload
                responsiveVariant={{
                  desktopUp: mapImageVariant(slide, "desktopUp"),
                  mobileUp: mapImageVariant(slide, "mobileUp"),
                  tabletUp: mapImageVariant(slide, "tabletUp"),
                }}
                src={slide.url}
                {...bindResponsiveAspectRatio}
              />
            </SwiperSlide>
          ))}
        </StyledSwiper>

        <CarouselControls
          activeIndex={activeIndex}
          onDotClick={handleDotClick}
          onNext={handleNext}
          onPrevious={handlePrevious}
          totalSlides={carouselCount}
        />
      </>
    );
  }

  return (
    <Figure {...bindResponsiveAspectRatio} className={className}>
      {displayImage?.url ? (
        <BaseImage
          $objectFit="cover"
          alt={imageAlt}
          responsiveVariant={responsiveVariant}
          src={displayImage?.url}
          {...bindResponsiveAspectRatio}
          lazyload
        />
      ) : (
        <ContentPlaceholder $variant={variant} {...bindResponsiveAspectRatio} />
      )}

      {withCaption && displayImage?.title && (
        <StyledFigcaption>{displayImage?.title}</StyledFigcaption>
      )}
    </Figure>
  );
};

ContentCoverImage.displayName = "ContentCoverImage";
